import _ from 'lodash';
import axios from 'axios';
import { PageWidget } from 'components/Editor/widgets/page';
import {
  DESKTOP_TO_MOBILE_OPTIMIZE, 
  DESKTOP_TO_MOBILE_OPTIMIZE_REQUEST, DESKTOP_TO_MOBILE_OPTIMIZE_SUCCEEDED, DESKTOP_TO_MOBILE_OPTIMIZE_FAILED,
  MOBILE_LAYOUT_OPTIMIZE,
  MOBILE_LAYOUT_OPTIMIZE_REQUEST, MOBILE_LAYOUT_OPTIMIZE_SUCCEEDED, MOBILE_LAYOUT_OPTIMIZE_FAILED,
  DESKTOP_TO_MOBILE_PLAN_AND_OPTIMIZE,
  DESKTOP_TO_MOBILE_PLAN_AND_OPTIMIZE_REQUEST, DESKTOP_TO_MOBILE_PLAN_AND_OPTIMIZE_SUCCEEDED, DESKTOP_TO_MOBILE_PLAN_AND_OPTIMIZE_FAILED
} from 'constants/actionTypes.jsx';

import { applyWidgetPropertyChanges } from 'actions/editor.jsx';
import { generateHTML, generateImageSnapshot, generateImageSnapshotFromHTML } from 'components/Editor/renderer/generator';
import { generateLayoutChanges, generateLayoutChangesFromCritique } from 'components/Editor/renderer/layoutGenerator';
import { PAGE } from 'constants/widgetTypes';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import { createAction } from 'redux-actions';

const optimizeUrl = process.env.REACT_APP_CLEVERSTORY_SERVICE_URL;
const MOBILE_OPTIMIZE_URL = `${optimizeUrl}/cleverstory/1.0/mobile-optimize`;
const ITERATION_MOBILE = 1;

/**
 * Desktop to Mobile Plan and Optimize Action and Saga
*/
export const desktopToMobilePlanAndOptimize = (breakpoint, storyboardId, pageId, editorSession, pageWidget = null) => {
  return {
    type: DESKTOP_TO_MOBILE_PLAN_AND_OPTIMIZE,
    payload: {
      breakpoint: breakpoint,
      storyboardId: storyboardId,
      pageId: pageId,
      editorSession: editorSession,
      pageWidget: pageWidget
    },
  };
}

export function* desktopToMobilePlanAndOptimizeSaga(action) {
  const { breakpoint, storyboardId, pageId, editorSession, pageWidget } = action.payload;

  const widgets = yield select(state => _.get(state, `${editorSession}.present.widgets`) || []);
  let pageWidgets;

  if (pageWidget) {
    const currentPageWidget = widgets.find(w => w.uid === pageWidget.uid);
    pageWidgets = [currentPageWidget];
  } else {
    pageWidgets = widgets.filter(w => w.type === PAGE);
  }

  for (const pageWidget of pageWidgets) {
    yield call(planAndOptimizeDestopToMobileForPageWidgetSaga,
      pageWidget,
      widgets,
      breakpoint,
      storyboardId,
      pageId,
      editorSession
    );
  }
}

function* planAndOptimizeDestopToMobileForPageWidgetSaga(pageWidget, widgets, breakpoint, storyboardId, pageId, editorSession) {
  // Implementation for planning and optimizing desktop to mobile for a page widget

  const filteredWidgets = widgets
    .filter(widget => widget.parent != null)
    .sort((a, b) => a.order - b.order);

  const desktopHTML = yield call(generateHTML, filteredWidgets, pageWidget, "desktop", storyboardId);
  const mobileHTML = yield call(generateHTML, filteredWidgets, pageWidget, "mobile", storyboardId);

  const desktopImage = yield call(
    generateImageSnapshot,
    pageWidget,
    'desktop'
  );

  const mobileImage = yield call(
    generateImageSnapshot,
    pageWidget,
    'mobile'
  );

  const request = {
    storyboardId: storyboardId,
    pageId: pageId,
    pageWidgetId: pageWidget.uid,
    pageWidget: pageWidget,
    view: "desktop",
    desktopHtml: desktopHTML,
    mobileHtml: mobileHTML,
  }

  const formData = new FormData();
  formData.append(
    "request",
    new Blob([JSON.stringify(request)], {
      type: "application/json"
    })
  );
  formData.append("desktopImage", desktopImage);
  formData.append("mobileImage", mobileImage);

  yield put(planAndOptimizeDesktopToMobile(editorSession, formData, request));
}

export const planAndOptimizeDesktopToMobile = createAction(DESKTOP_TO_MOBILE_PLAN_AND_OPTIMIZE_REQUEST, (editorSession, request ,rawRequest) => {
  return {
    editorSession: editorSession,
    request: request,
    rawRequest: rawRequest
  }
});

export const planAndOptimizeDesktopToMobileSucceeded = createAction(DESKTOP_TO_MOBILE_PLAN_AND_OPTIMIZE_SUCCEEDED, (editorSession, response ,rawRequest) => {
  return {
    editorSession: editorSession,
    response: response,
    rawRequest: rawRequest
  }
});

export const planAndOptimizeDesktopToMobileFailed = createAction(DESKTOP_TO_MOBILE_PLAN_AND_OPTIMIZE_FAILED, (editorSession, error, rawRequest) => {
  return {
    editorSession: editorSession,
    error: error,
    rawRequest: rawRequest
  }
});

function* planAndOptimizeDesktopToMobileSaga(action) {
  const { editorSession, request, rawRequest } = action.payload;
  const URL = `${MOBILE_OPTIMIZE_URL}/sequential/desktop-planning`;

  const planAndOptimizeDesktopToMobileAPI = () => {
    return axios.post(URL, request, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    }).then(response => response.data);
  };

  try {
    const response = yield call(planAndOptimizeDesktopToMobileAPI);
    yield put(planAndOptimizeDesktopToMobileSucceeded(editorSession, response, rawRequest));
    const htmlString = response.mobileHtml;
    if (htmlString) {
      const widgets = yield select(state => _.get(state, `${editorSession}.present.widgets`) || []);
      const mobileLayoutChanges = generateLayoutChangesFromCritique(response.mobileHtml, widgets);
      yield put(applyWidgetPropertyChanges(editorSession, mobileLayoutChanges, "mobile"));
      yield put(mobileLayoutOptimize(rawRequest.storyboardId, rawRequest.pageId, editorSession, rawRequest.pageWidget, 1));
    }
  } catch (error) {
    yield put(planAndOptimizeDesktopToMobileFailed(editorSession, error, rawRequest));
  }
}

/**
 * Desktop to Mobile Optimize Action and Saga
*/
export const desktopToMobileOptimize = (breakpoint, storyboardId, pageId, editorSession, pageWidget = null) => {
  return {
    type: DESKTOP_TO_MOBILE_OPTIMIZE,
    payload: {
      breakpoint: breakpoint,
      storyboardId: storyboardId,
      pageId: pageId,
      editorSession: editorSession,
      pageWidget: pageWidget
    },
  };
}

export function* desktopToMobileOptimizeSaga(action) {
  const { breakpoint, storyboardId, pageId, editorSession, pageWidget } = action.payload;

  const widgets = yield select(state => _.get(state, `${editorSession}.present.widgets`) || []);
  let pageWidgets; 

  if (pageWidget) {
    const currentPageWidget = widgets.find(w => w.uid === pageWidget.uid);
    pageWidgets = [currentPageWidget];
  } else {
    pageWidgets = widgets.filter(w => w.type === PAGE);
  }

  for (const pageWidget of pageWidgets) {
    yield call(optimizeDesktopToMobileForPageWidgetSaga,
      pageWidget,
      widgets,
      breakpoint,
      storyboardId,
      pageId,
      editorSession
    );
  }
}

function* optimizeDesktopToMobileForPageWidgetSaga(pageWidget, widgets, breakpoint, storyboardId, pageId, editorSession) {
  const filteredWidgets = widgets
    .filter(widget => widget.parent != null)
    .sort((a, b) => a.order - b.order);

  const availableFonts = yield select(state => {
    const fonts = _.get(state, 'fonts.items') || [];
    const installed = _.get(state, 'fonts.installed') || [];
    
    return fonts
      .filter(font => installed.some(item => item === font.id))
      .filter(font => font.styleSheet != null);
  });

  const desktopHTML = yield call(generateHTML, filteredWidgets, pageWidget, "desktop", storyboardId, availableFonts);
  const mobileHTML = yield call(generateHTML, filteredWidgets, pageWidget, "mobile", storyboardId, availableFonts);

  const desktopImage = yield call(
    generateImageSnapshotFromHTML,
    desktopHTML,
    'desktop'
  );

  const mobileImage = yield call(
    generateImageSnapshotFromHTML,
    mobileHTML,
    'mobile'
  );

  const request = {
    storyboardId: storyboardId,
    pageId: pageId,
    pageWidgetId: pageWidget.uid,
    pageWidget: pageWidget,
    view: "desktop",
    desktopHtml: desktopHTML,
    mobileHtml: mobileHTML,
  }

  const formData = new FormData();
  formData.append(
    "request",
    new Blob([JSON.stringify(request)], {
      type: "application/json"
    })
  );
  formData.append("desktopImage", desktopImage);
  formData.append("mobileImage", mobileImage);

  const response = yield put(optimizeDesktopToMobile(editorSession, formData, request));
}


export const optimizeDesktopToMobile = createAction(DESKTOP_TO_MOBILE_OPTIMIZE_REQUEST, (editorSession ,request, rawRequest) => {
  return {
    editorSession: editorSession,
    request: request,
    rawRequest: rawRequest
  }
});

export const optimizeDesktopToMobileSucceeded = createAction(DESKTOP_TO_MOBILE_OPTIMIZE_SUCCEEDED, (editorSession ,response, rawRequest) => {
  return {
    editorSession: editorSession,
    response: response,
    rawRequest: rawRequest
  }
});

export const optimizeDesktopToMobileFailed = createAction(DESKTOP_TO_MOBILE_OPTIMIZE_FAILED, (editorSession ,error, rawRequest) => {
  return {
    editorSession: editorSession,
    error: error,
    rawRequest: rawRequest
  }
});


function* optimizeDesktopToMobileSaga(action) {
  const { editorSession, request, rawRequest } = action.payload;
  // const URL = `${MOBILE_OPTIMIZE_URL}/sequential/desktop-critique`;
  const URL = `${MOBILE_OPTIMIZE_URL}/sequential/desktop-planning`;

  const optimizeDesktopToMobileAPI = () => {
    return axios.post(URL, request, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    }).then(response => response.data);
  };

  try {
    const response = yield call(optimizeDesktopToMobileAPI);
    yield put(optimizeDesktopToMobileSucceeded(editorSession, response, rawRequest));
    const htmlString = response.mobileHtml;
    if (htmlString) {
      const critiqueResponse = JSON.parse(htmlString);
      const widgets = yield select(state => _.get(state, `${editorSession}.present.widgets`) || []);
      const mobileLayoutChanges = generateLayoutChangesFromCritique(editorSession, critiqueResponse, widgets);
      yield put(applyWidgetPropertyChanges(editorSession, mobileLayoutChanges, "mobile"));
      yield put(mobileLayoutOptimize(rawRequest.storyboardId, rawRequest.pageId, editorSession, rawRequest.pageWidget, 1));
    }
    // if (htmlString) {
    //   const mobileLayoutChanges = generateLayoutChanges(htmlString);
    //   yield put(applyWidgetPropertyChanges(editorSession, mobileLayoutChanges, "mobile"));
    //   yield put(mobileLayoutOptimize(rawRequest.storyboardId, rawRequest.pageId, editorSession, rawRequest.pageWidget, 1));
    // }
  } catch (error) {
    yield put(optimizeDesktopToMobileFailed(editorSession, error, rawRequest));
  }
}

/**
 * Mobile Layout Optimize Action and Saga
*/
export const mobileLayoutOptimize = (storyboardId, pageId, editorSession, pageWidget = null, iteration = 1) => {
  return {
    type: MOBILE_LAYOUT_OPTIMIZE,
    payload: {
      storyboardId: storyboardId,
      pageId: pageId,
      editorSession: editorSession,
      pageWidget: pageWidget,
      iteration: iteration
    },
  };
}

export function* mobileLayoutOptimizeSaga(action) {
  const { storyboardId, pageId, editorSession, pageWidget, iteration } = action.payload;
  
  const widgets = yield select(state => _.get(state, `${editorSession}.present.widgets`) || []);
  let pageWidgets; 

  if (pageWidget) {
    const currentPageWidget = widgets.find(w => w.uid === pageWidget.uid);
    pageWidgets = [currentPageWidget];
  } else {
    pageWidgets = widgets.filter(w => w.type === PAGE);
  }

  for (const pageWidget of pageWidgets) {
    yield call(optimizeMobileLayoutForPageWidgetSaga,
      pageWidget,
      widgets,
      storyboardId,
      pageId,
      editorSession,
      iteration
    );
  }
}

function* optimizeMobileLayoutForPageWidgetSaga(pageWidget, widgets, storyboardId, pageId, editorSession, iteration) {
  const filteredWidgets = widgets
    .filter(widget => widget.parent != null)
    .sort((a, b) => a.order - b.order);

  const availableFonts = yield select(state => {
      const fonts = _.get(state, 'fonts.items') || [];
      const installed = _.get(state, 'fonts.installed') || [];
      
      return fonts
        .filter(font => installed.some(item => item === font.id))
        .filter(font => font.styleSheet != null);
  });

  const mobileHTML = yield call(generateHTML, filteredWidgets, pageWidget, "mobile", storyboardId, availableFonts);

  const mobileImage = yield call(
    generateImageSnapshotFromHTML,
    mobileHTML,
    'mobile'
  );

  const request = {
    storyboardId: storyboardId,
    pageId: pageId,
    pageWidgetId: pageWidget.uid,
    view: "mobile",
    mobileHtml: mobileHTML,
  }

  const formData = new FormData();
  formData.append(
    "request",
    new Blob([JSON.stringify(request)], {
      type: "application/json"
    })
  );
  formData.append("mobileImage", mobileImage);

  const response = yield put(optimizeMobileLayout(editorSession, formData, request, iteration));
}

export const optimizeMobileLayout = createAction(MOBILE_LAYOUT_OPTIMIZE_REQUEST, (editorSession ,request, rawRequest, iteration) => {
  return {
    editorSession: editorSession,
    request: request,
    rawRequest: rawRequest,
    iteration: iteration
  }
});

export const optimizeMobileLayoutSucceeded = createAction(MOBILE_LAYOUT_OPTIMIZE_SUCCEEDED, (editorSession ,response, rawRequest) => {
  return {
    editorSession: editorSession,
    response: response,
    rawRequest: rawRequest
  }
});

export const optimizeMobileLayoutFailed = createAction(MOBILE_LAYOUT_OPTIMIZE_FAILED, (editorSession ,error, rawRequest) => {
  return {
    editorSession: editorSession,
    error: error,
    rawRequest: rawRequest
  }
});

function* optimizeMobileLayoutSaga(action) {
  const { editorSession, request, rawRequest, iteration } = action.payload;
  const URL = `${MOBILE_OPTIMIZE_URL}/sequential/mobile-planning`;

  const optimizeMobileLayoutAPI = () => {
    return axios.post(URL, request, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    }).then(response => response.data);
  }

  try {
    const response = yield call(optimizeMobileLayoutAPI);
    yield put(optimizeMobileLayoutSucceeded(editorSession, response, rawRequest));
    const htmlString = response.mobileHtml;
    if (htmlString) {
      const critiqueResponse = JSON.parse(htmlString);
      const widgets = yield select(state => _.get(state, `${editorSession}.present.widgets`) || []);
      const mobileLayoutChanges = generateLayoutChangesFromCritique(editorSession, critiqueResponse, widgets);
      yield put(applyWidgetPropertyChanges(editorSession, mobileLayoutChanges, "mobile"));
    }
    // if (htmlString) {
    //   const mobileLayoutChanges = generateLayoutChanges(htmlString);
    //   yield put(applyWidgetPropertyChanges(editorSession, mobileLayoutChanges, "mobile"));
    // }
    if (iteration < ITERATION_MOBILE) {
      yield put(
        mobileLayoutOptimize(
          rawRequest.storyboardId,
          rawRequest.pageId,
          editorSession,
          rawRequest.pageWidget,
          iteration + 1
        )
      );
    }
  } catch (error) {
    yield put(optimizeMobileLayoutFailed(editorSession, error, rawRequest));
  }
}

export default function* mobileOptimizationSaga() {
  yield takeEvery(DESKTOP_TO_MOBILE_OPTIMIZE, desktopToMobileOptimizeSaga);
  yield takeEvery(MOBILE_LAYOUT_OPTIMIZE, mobileLayoutOptimizeSaga);
  yield takeEvery(DESKTOP_TO_MOBILE_OPTIMIZE_REQUEST, optimizeDesktopToMobileSaga);
  yield takeEvery(MOBILE_LAYOUT_OPTIMIZE_REQUEST, optimizeMobileLayoutSaga);
  yield takeEvery(DESKTOP_TO_MOBILE_PLAN_AND_OPTIMIZE, desktopToMobilePlanAndOptimizeSaga);
  yield takeEvery(DESKTOP_TO_MOBILE_PLAN_AND_OPTIMIZE_REQUEST, planAndOptimizeDesktopToMobileSaga);
}